import { NonceHelper } from "@/utils/helpers/nonce-helper";
import { PhoneHelper } from "@/utils/helpers/phone-helper";
import SubmitButton from "@components/core/buttons/SubmitButton";
import InputFormField from "@components/core/inputs/InputFormField";
import FormFieldLabel from "@components/core/labels/FormFieldLabel";
import { useContacts } from "@state/contact/ContactsHook";
import { Contact, Signatory } from "@type/core/dashboard/dashboard.types";
import { formValidateTriggers, MAX_SIGNATORIES } from "@utils/Constant";
import { formatCategory } from "@utils/helpers/format-helper";
import { getContactLabel } from "@utils/helpers/text-helper";
import type { FormInstance, SelectProps } from "antd";
import { Checkbox, Form, Select, Space, Spin, Tooltip } from "antd";
import { Dispatch, FunctionComponent, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import AutoSizer from "react-virtualized-auto-sizer";
import { VariableSizeList } from "react-window";
import ExclamationTriangle from "@components/core/badges/ExclamationTriangle";

function sortContacts(a: Contact, b: Contact) {
  if (a.lastname < b.lastname) {
    return -1;
  }
  if (a.lastname > b.lastname) {
    return 1;
  }
  if (a.firstname < b.firstname) {
    return -1;
  }
  if (a.firstname > b.firstname) {
    return 1;
  }
  return 0;
}

function filterField(field: string, search: string) {
  return field?.toLowerCase().includes(search.toLowerCase());
}

function filterCategoryField(
  category: string | undefined,
  categorySearch: string[],
) {
  let categories: string[] = [];
  if (category) {
    categories = category.toUpperCase().split("|");
  }
  if (categorySearch.length) {
    return categorySearch.every((search) =>
      categories.some((cat) => cat.includes(search)),
    );
  }
  return true;
}

interface AddSignatoryFormProps {
  searchDisabled?: boolean;
  fromMembers?: boolean;
  className?: string;
  baseContacts?: Contact[];
  setSignatories: (signatories: Signatory[]) => void;
  setIsModalVisible: Dispatch<SetStateAction<boolean>>;
  signatories: Signatory[];
  form: FormInstance<any>;
  fieldsSearch: string;
  setFieldsSearch: Dispatch<SetStateAction<string>>;
  categoriesSearch?: string[];
  setCategoriesSearch?: Dispatch<SetStateAction<string[]>>;
}

const AddSignatoryForm: FunctionComponent<AddSignatoryFormProps> = ({
  searchDisabled = false,
  fromMembers = false,
  className = "",
  baseContacts,
  setSignatories,
  setIsModalVisible,
  signatories,
  form,
  fieldsSearch,
  setFieldsSearch,
  categoriesSearch = [],
  setCategoriesSearch,
}) => {
  const { t } = useTranslation();
  const { contactForm } = useContacts();
  const [isLoading, setIsLoading] = useState(false);

  const filterDisplayedContact = (contact: Contact) => {
    return (
      (filterField(contact.firstname, fieldsSearch) ||
        filterField(contact.lastname, fieldsSearch) ||
        filterField(contact.email, fieldsSearch) ||
        filterField(contact.phone, fieldsSearch) ||
        filterField(contact.companyName, fieldsSearch)) &&
      filterCategoryField(contact.category, categoriesSearch) &&
      !signatories.find(({ email }) => email === contact.email)
    );
  };
  const filteredContacts = (baseContacts ?? [])
    .filter((contact) => filterDisplayedContact(contact))
    .sort(sortContacts);
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const officeName = "";

  const selectOptions: SelectProps["options"] = [];
  contactForm?.category?.forEach((category) =>
    selectOptions.push({
      label: category.name,
      value: category.name,
    }),
  );

  const handleOnChangeCategory = (selectedCategories: string[]) => {
    if (setCategoriesSearch) {
      setCategoriesSearch(selectedCategories);
    }
  };

  const InitialValues = {
    search: "",
  };

  const handleSubmit = () => {
    setIsLoading(true);
    const expertsToAdd =
      baseContacts?.filter((contact) =>
        selectedEmails.includes(contact.email),
      ) ?? [];
    const newSignatories = expertsToAdd.map((expert) => {
      const newSignatory: Signatory = {
        key: expert.email + Date.now().toString(),
        id: expert.id ?? "",
        firstname: expert.firstname,
        lastname: expert.lastname,
        email: expert.email,
        phone: expert.phone,
        officeId: expert.officeId,
      };
      return newSignatory;
    });
    setSignatories([...signatories, ...newSignatories]);
    form.resetFields();
    if (setCategoriesSearch) {
      setCategoriesSearch([]);
    }
    setFieldsSearch("");
    setSelectedEmails([]);
    setIsModalVisible(false);
    setIsLoading(false);
  };

  const maxSignatoriesExceeded: boolean =
    selectedEmails.length + signatories.length > MAX_SIGNATORIES;

  return (
    <Form
      className={`app-form-add-signatory flex-grow-1 d-flex flex-column ${className}`}
      form={form}
      onFinish={handleSubmit}
      initialValues={InitialValues}
      {...formValidateTriggers}
      layout="vertical"
    >
      {maxSignatoriesExceeded && (
        <ExclamationTriangle
          text={t("signature.accordion.signatoryForm.tooMany")}
        />
      )}
      <InputFormField
        showLabel
        module="signature.addContactModal"
        field="search"
        disabled={searchDisabled}
        onChange={(ev) => {
          ev.preventDefault();
          setFieldsSearch(ev.target.value);
        }}
      />
      {!fromMembers && (
        <>
          {
            <>
              <FormFieldLabel
                field={"categorySearch"}
                label={t(
                  "signature.addContactModal.fields.categorySearch.label",
                )}
              />
              <Space className="w-100" direction="vertical">
                <Select
                  className="w-100"
                  mode="multiple"
                  allowClear
                  placeholder="Catégories"
                  defaultValue={[]}
                  onChange={handleOnChangeCategory}
                  options={selectOptions}
                />
              </Space>
            </>
          }
        </>
      )}
      <div className="my-8">
        <label className="d-block text-dark mb-8" htmlFor="exampleSelectMulti">
          {t("signature.addContactModal.selection")}
        </label>
        <div className="checkbox-group-container">
          {!isLoading ? (
            <AutoSizer nonce={NonceHelper.getNonceValue()}>
              {({ height, width }) => (
                <VariableSizeList
                  height={height}
                  itemCount={filteredContacts.length}
                  itemSize={() => 30}
                  width={width}
                >
                  {({ index, style }) => {
                    const contact = filteredContacts[index];
                    return (
                      // If style-src 'unsafe-inline' is remove for zendesk, we will have to modify this in order not to have injected style (which means that we need to override node_modules/react-window
                      <div
                        style={style}
                        key={`filteredContact_${contact.firstname}_${index}`}
                        data-test="input-label"
                        data-alt={`${contact.firstname} ${contact.lastname?.toUpperCase() || ""} - ${contact.email}`}
                        data-test-for="addMember"
                      >
                        <Checkbox
                          checked={selectedEmails.includes(contact.email)}
                          data-test="addMember"
                          onChange={() => {
                            if (selectedEmails.includes(contact.email)) {
                              setSelectedEmails(
                                selectedEmails.filter(
                                  (e) => e !== contact.email,
                                ),
                              );
                            } else {
                              setSelectedEmails([
                                ...selectedEmails,
                                contact.email,
                              ]);
                            }
                          }}
                          className="mt-4 checkbox-nowrap"
                        >
                          <Tooltip
                            title={
                              <>
                                <span>{`${contact.firstname} ${contact.lastname}`}</span>
                                <br />
                                <span>{contact.email}</span>
                                <br />
                                <span>
                                  {PhoneHelper.formatPhone(contact.phone)}
                                </span>
                                <br />
                                {officeName && (
                                  <>
                                    <span>{officeName}</span>
                                    <br />
                                  </>
                                )}
                                {contact.category && (
                                  <div className="mt-2">
                                    {formatCategory(contact.category)}
                                  </div>
                                )}
                              </>
                            }
                          >
                            {getContactLabel(contact)}
                          </Tooltip>
                        </Checkbox>
                      </div>
                    );
                  }}
                </VariableSizeList>
              )}
            </AutoSizer>
          ) : (
            <div className="h-100 flex-grow-1 d-flex align-items-center justify-content-center">
              <Spin />
            </div>
          )}
        </div>
      </div>
      <div className="flex-grow-1 d-flex align-items-end justify-content-center flex-wrap gap-8 mt-16 mb-24">
        <SubmitButton
          disabled={
            selectedEmails.length === 0 || isLoading || maxSignatoriesExceeded
          }
          label="signature.addContactModal.validate"
          className="mw-160"
        />
      </div>
    </Form>
  );
};

export default AddSignatoryForm;
